import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Box,
  CheckCircle2,
  Globe2,
  Mail,
  MapPin,
  Package,
  Phone,
  Ship,
  Tag,
} from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import Seo, { breadcrumbSchema, SITE_URL } from '../components/Seo';
import {
  ABOUT_IMAGES,
  CERTIFICATIONS,
  COMPANY_OVERVIEW,
  COMPANY_PROFILE,
  COMPANY_DETAILS,
  COMPANY_STATS,
  CONTACT_DETAILS,
  CORE_VALUES,
  EXPORT_SERVICES,
} from '../data/aboutData';

import '../Home.css';
import '../PageBanner.css';
import '../AboutUs.css';

const SERVICE_ICONS = {
  package: Package,
  tag: Tag,
  box: Box,
  ship: Ship,
};

function AboutUsPage() {
  const description = 'Learn about Swarvinn Impex Ltd, a Tamil Nadu export company supplying quality Indian groceries, private-label packing and freight support to UK food businesses.';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="page about-v2">
      <Seo
        title="About Our India-to-UK Food Export Company"
        description={description}
        path="/about-us"
        image={ABOUT_IMAGES.hero}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Swarvinn Impex',
            url: `${SITE_URL}/about-us`,
            description,
            inLanguage: 'en-GB',
            about: { '@id': `${SITE_URL}/#organization` },
          },
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'About Us', path: '/about-us' },
          ]),
        ]}
      />
      <Header />
      <main className="main">
        <PageBanner
          image={ABOUT_IMAGES.hero}
          eyebrow="About Swarvinn Impex"
          tagline={COMPANY_PROFILE.tagline}
          title={COMPANY_PROFILE.legalName}
          lead={COMPANY_OVERVIEW.headline}
          breadcrumbs={[{ label: 'About Us' }]}
        >
          <div className="ab-hero-chips">
            <span>{COMPANY_PROFILE.headquarters}</span>
            <span>Est. {COMPANY_PROFILE.founded}</span>
            <span>Main market: United Kingdom</span>
          </div>
        </PageBanner>

        {/* Stats ribbon */}
        <section className="ab-ribbon" aria-label="Company highlights">
          <div className="ab-ribbon-inner">
            {COMPANY_STATS.map((stat, i) => (
              <div key={stat.label} className="ab-ribbon-item">
                {i > 0 && <span className="ab-ribbon-divider" aria-hidden="true" />}
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="ab-markets" aria-label="Export markets">
          <div className="ab-markets-inner">
            <div className="ab-markets-primary">
              <span className="ab-label">Main Export Country</span>
              <strong>{COMPANY_PROFILE.primaryMarket}</strong>
              <p>Our primary wholesale and retail focus — UK importers, cash &amp; carry, and food distributors.</p>
            </div>
            <div className="ab-markets-secondary">
              <span className="ab-label">Secondary Markets</span>
              <ul>
                {COMPANY_PROFILE.secondaryMarkets.map((market) => (
                  <li key={market}>{market}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="ab-story">
          <div className="ab-story-inner">
            <div className="ab-story-media">
              <img
                src={ABOUT_IMAGES.story}
                alt="Quality control inspection for export-grade Indian food products"
                loading="lazy"
              />
              <div className="ab-story-caption">
                <Globe2 size={18} />
                <span>Export-grade quality at every stage</span>
              </div>
            </div>
            <div className="ab-story-text">
              <span className="ab-label">Who We Are</span>
              <h2>Your trusted India-to-UK export partner</h2>
              {COMPANY_OVERVIEW.intro.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
              <Link to="/products" className="ab-link-btn">
                Explore our product range
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="ab-purpose">
          <div className="ab-purpose-inner">
            <article className="ab-purpose-card">
              <span className="ab-purpose-num">01</span>
              <h3>Our Mission</h3>
              <p>{COMPANY_OVERVIEW.mission}</p>
            </article>
            <article className="ab-purpose-card">
              <span className="ab-purpose-num">02</span>
              <h3>Our Vision</h3>
              <p>{COMPANY_OVERVIEW.vision}</p>
            </article>
          </div>
        </section>

        {/* Values */}
        <section className="ab-values">
          <div className="ab-section-head">
            <span className="ab-label">Core Values</span>
            <h2>What drives every shipment</h2>
          </div>
          <div className="ab-values-grid">
            {CORE_VALUES.map((value, i) => (
              <article key={value.title} className="ab-value-item">
                <span className="ab-value-num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{value.title}</h3>
                <p>{value.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Services */}
        <section className="ab-services" id="export-services">
          <div className="ab-services-layout">
            <div className="ab-services-intro ab-sticky-col">
              <span className="ab-label">Export Services</span>
              <h2>Built for UK trade — ready for other markets</h2>
              <p>
                From sourcing in Tamil Nadu to delivery at your UK depot — we handle
                wholesale supply, private label, packing, and freight coordination.
                Secondary markets across Europe, the Middle East, and beyond are supported on request.
              </p>
            </div>
            <div className="ab-services-list">
              {EXPORT_SERVICES.map((service) => {
                const Icon = SERVICE_ICONS[service.icon] || Package;
                return (
                  <article key={service.title} className="ab-service-row">
                    <div className="ab-service-icon">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3>{service.title}</h3>
                      <p>{service.desc}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Compliance + Profile */}
        <section className="ab-profile" id="certifications">
          <div className="ab-profile-inner">
            <div className="ab-profile-compliance">
              <span className="ab-label">Compliance</span>
              <h2>Export-ready documentation</h2>
              <p>
                Every product line is prepared with the documentation UK importers expect.
              </p>
              <ul className="ab-cert-list">
                {CERTIFICATIONS.map((cert) => (
                  <li key={cert}>
                    <CheckCircle2 size={18} />
                    {cert}
                  </li>
                ))}
              </ul>
            </div>
            <aside className="ab-profile-aside ab-sticky-col">
              <div className="ab-profile-card">
              <h3>Company Profile</h3>
              <dl>
                {COMPANY_DETAILS.map((row) => (
                  <div key={row.label} className="ab-profile-row">
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
              </div>
            </aside>
          </div>
        </section>

        {/* CTA */}
        <section className="ab-cta">
          <div className="ab-cta-inner">
            <h2>Ready to import from India?</h2>
            <p>Speak with our export team about wholesale pricing, private label, and UK freight.</p>
            <Link to="/contact" className="ab-cta-btn">
              Request UK Import Quote
              <ArrowRight size={16} />
            </Link>
            <div className="ab-cta-contact">
              <a href={`tel:${CONTACT_DETAILS.phone.replace(/\s/g, '')}`}>
                <Phone size={15} />
                {CONTACT_DETAILS.phone}
              </a>
              <a href={`mailto:${CONTACT_DETAILS.email}`}>
                <Mail size={15} />
                {CONTACT_DETAILS.email}
              </a>
              <span>
                <MapPin size={15} />
                {CONTACT_DETAILS.address}
              </span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default AboutUsPage;
